"use client";

import Link from "next/link";
import { useRouter } from "next/navigation";
import { MdKeyboardArrowDown } from "react-icons/md";
import { useState } from "react";
import FlotsIconDark from "@/public/FlotsIconDark.svg";
import { FaArrowRight, FaUser, FaBuilding } from "react-icons/fa6";
import { FormattedMessage, useIntl } from "react-intl";
import LanguageSwitcher from "./LanguageSwitcher";
import { trackCtaClick, trackEvent } from "@/app/providers/ObservabilityProvider";
import { useAppLink } from "@/lib/analytics/app-links";
import { CATEGORY_META, CATEGORIES } from "@/app/components/root/Ressources/category-meta";
import { categoryToSlug } from "@/lib/category-slugs";
import { homePath, isHomePath } from "@/lib/locale-routing";
import { navigateToProfileSection, navigateToSection } from "./profileNav";
import { useLocale } from "@/app/providers/IntlProvider";

type NavFeatureItem = {
  titleKey: string;
  descKey: string;
  profile: "Individual" | "Organization";
  scrollTo: string;
};

const INDIV_ITEMS: NavFeatureItem[] = [
  { titleKey: "nav.story.tasks.title", descKey: "nav.story.tasks.desc", profile: "Individual", scrollTo: "story-build" },
  { titleKey: "nav.story.notes.title", descKey: "nav.story.notes.desc", profile: "Individual", scrollTo: "story-action" },
  { titleKey: "nav.story.planday.title", descKey: "nav.story.planday.desc", profile: "Individual", scrollTo: "profile-planday" },
];

const ORG_ITEMS: NavFeatureItem[] = [
  { titleKey: "nav.story.privacy.title", descKey: "nav.story.privacy.desc", profile: "Organization", scrollTo: "profile-privacy" },
  { titleKey: "nav.story.delegate.title", descKey: "nav.story.delegate.desc", profile: "Organization", scrollTo: "profile-delegate" },
  { titleKey: "nav.story.live.title", descKey: "nav.story.live.desc", profile: "Organization", scrollTo: "profile-live" },
];

const DesktopNavbar = ({ currentRoute }: { currentRoute: string }) => {
  const intl = useIntl();
  const router = useRouter();
  const { locale } = useLocale();
  const ressourcesSuffix = locale === "en" ? "/en" : "";
  const [isFeaturesOpen, setIsFeaturesOpen] = useState(false);
  const [isRessourcesOpen, setIsRessourcesOpen] = useState(false);
  const { href: appUrl, target: appTarget } = useAppLink("navbar");

  const dispatchProfileNav = (profile: "Individual" | "Organization", scrollTo: string) =>
    navigateToProfileSection(currentRoute, router, profile, scrollTo, locale);

  const dispatchSectionNav = (profile: "Individual" | "Organization", scrollTo: string) =>
    navigateToSection(currentRoute, router, profile, scrollTo, locale);

  /**
   * Keyboard side of a hover dropdown (disclosure pattern): it closes when focus leaves its
   * item, and Escape closes it and returns focus to its trigger.
   */
  const dropdownKeys = (close: () => void) => ({
    onBlur: (e: React.FocusEvent<HTMLLIElement>) => {
      if (!e.currentTarget.contains(e.relatedTarget as Node | null)) close();
    },
    onKeyDown: (e: React.KeyboardEvent<HTMLLIElement>) => {
      if (e.key !== "Escape") return;
      close();
      e.currentTarget.querySelector<HTMLElement>("[aria-expanded]")?.focus();
    },
  });

  return (
    <nav
      id="navbar"
      aria-label={intl.formatMessage({ id: "a11y.mainNav" })}
      className="w-6/7 lg:w-5/6 xl:w-3/4 min-w-200 max-w-300 h-14 select-none mx-auto fixed left-1/2 -translate-x-1/2 top-0 z-50 flex items-center justify-between bg-surface px-1 mt-12 ld:mt-8 shadow-xl shadow-black/3 rounded-full"
      style={{ top: "var(--promo-h, 0px)" }}
    >
      <Link
        href={homePath(locale)}
        onClick={(e) => {
          if (isHomePath(currentRoute)) {
            e.preventDefault();
            window.scrollTo({ top: 0, behavior: "smooth" });
          }
        }}
        className="flex justify-center items-center gap-2 text-slate-800 ml-4"
        aria-label={intl.formatMessage({ id: "a11y.brandHome" })}
      >
        <FlotsIconDark className="logo-mark w-8 h-8" aria-hidden="true" />
        <p className="text-xl font-bold italic" aria-hidden="true">flots</p>
      </Link>

      <ul className="flex gap-1 xl:gap-1.5 items-center rounded-lg p-1.5 text-slate-800 text-sm pointer-events-auto">

        {/* Features dropdown */}
        <li
          className="relative flex items-center"
          onMouseEnter={() => setIsFeaturesOpen(true)}
          onMouseLeave={() => setIsFeaturesOpen(false)}
          {...dropdownKeys(() => setIsFeaturesOpen(false))}
        >
          {/* Hover opens it for the mouse; a click (or Enter / Space) toggles it for keyboards and touch. */}
          <button
            type="button"
            onClick={(e) => {
              trackEvent("navbar-features-link");
              // Keyboard activation (detail 0) toggles; a mouse click lands on a menu the
              // hover already opened, so it keeps it open instead of closing it.
              setIsFeaturesOpen((open) => (e.detail === 0 ? !open : true));
            }}
            aria-expanded={isFeaturesOpen}
            aria-controls={isFeaturesOpen ? "navbar-features-panel" : undefined}
            className="flex gap-2 items-center justify-center hover:bg-slate-100 transition-colors duration-300 px-3.5 xl:px-4 py-2 rounded-full cursor-pointer"
          >
            <FormattedMessage id="nav.features" />
            <MdKeyboardArrowDown aria-hidden="true" className={`text-lg transition-transform duration-200 ${isFeaturesOpen ? "rotate-180" : ""}`} />
          </button>

          {isFeaturesOpen && (
            <div id="navbar-features-panel" className="absolute top-full left-1/2 -translate-x-1/2 pt-2 z-50">
              <div className="bg-surface shadow-xl rounded-2xl border border-gray-100 p-4 flex gap-6 min-w-[540px]">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-3 px-2">
                    <FaUser className="text-brand text-xs" />
                    <span className="text-xs font-bold text-brand uppercase tracking-wide">
                      <FormattedMessage id="nav.features.indiv.label" />
                    </span>
                  </div>
                  <ul className="flex flex-col gap-1">
                    {INDIV_ITEMS.map((item) => (
                      <li key={item.scrollTo}>
                        <button
                          onClick={() => { trackEvent("navbar-feature-item", { scrollTo: item.scrollTo }); dispatchProfileNav(item.profile, item.scrollTo); setIsFeaturesOpen(false); }}
                          className="w-full text-left px-3 py-2.5 rounded-xl hover:bg-blue-ulight transition-colors duration-150 group/item cursor-pointer"
                        >
                          <p className="text-sm font-semibold text-text-main group-hover/item:text-brand transition-colors leading-tight mb-0.5">
                            <FormattedMessage id={item.titleKey} />
                          </p>
                          <p className="text-xs text-gray-main"><FormattedMessage id={item.descKey} /></p>
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="w-px bg-gray-100 self-stretch" />
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-3 px-2">
                    <FaBuilding className="text-brand text-xs" />
                    <span className="text-xs font-bold text-brand uppercase tracking-wide">
                      <FormattedMessage id="nav.features.org.label" />
                    </span>
                  </div>
                  <ul className="flex flex-col gap-1">
                    {ORG_ITEMS.map((item) => (
                      <li key={item.scrollTo}>
                        <button
                          onClick={() => { trackEvent("navbar-feature-item", { scrollTo: item.scrollTo }); dispatchProfileNav(item.profile, item.scrollTo); setIsFeaturesOpen(false); }}
                          className="w-full text-left px-3 py-2.5 rounded-xl hover:bg-blue-ulight transition-colors duration-150 group/item cursor-pointer"
                        >
                          <p className="text-sm font-semibold text-text-main group-hover/item:text-brand transition-colors leading-tight mb-0.5">
                            <FormattedMessage id={item.titleKey} />
                          </p>
                          <p className="text-xs text-gray-main"><FormattedMessage id={item.descKey} /></p>
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}
        </li>

        <li className="flex items-center">
          <button
            onClick={() => { trackEvent("navbar-pricing-link"); dispatchSectionNav("Individual", "pricing"); }}
            className="flex items-center whitespace-nowrap justify-center hover:bg-slate-100 transition-colors duration-300 px-3.5 xl:px-4 py-2 rounded-full cursor-pointer"
          >
            <FormattedMessage id="nav.pricing" />
          </button>
        </li>

        <li className="flex items-center">
          <button
            onClick={() => { trackEvent("navbar-contact-link"); dispatchSectionNav("Individual", "contact"); }}
            className="flex items-center whitespace-nowrap justify-center hover:bg-slate-100 transition-colors duration-300 px-3.5 xl:px-4 py-2 rounded-full cursor-pointer"
          >
            <FormattedMessage id="nav.contact" />
          </button>
        </li>

        {/* Ressources dropdown — label is a Link, hover reveals category list */}
        <li
          className="relative flex items-center"
          onMouseEnter={() => setIsRessourcesOpen(true)}
          onMouseLeave={() => setIsRessourcesOpen(false)}
          // The label is a link (to the hub), so keyboards open the category list on focus;
          // it closes when focus leaves the item or on Escape.
          onFocus={() => setIsRessourcesOpen(true)}
          {...dropdownKeys(() => setIsRessourcesOpen(false))}
        >
          <Link
            href={`/resources${ressourcesSuffix}`}
            onClick={() => { trackEvent("navbar-ressources-link"); setIsRessourcesOpen(false); }}
            aria-expanded={isRessourcesOpen}
            aria-controls={isRessourcesOpen ? "navbar-ressources-panel" : undefined}
            className="flex gap-2 items-center justify-center hover:bg-slate-100 transition-colors duration-300 px-3.5 xl:px-4 py-2 rounded-full cursor-pointer"
          >
            <FormattedMessage id="nav.ressources" />
            <MdKeyboardArrowDown aria-hidden="true" className={`text-lg transition-transform duration-200 ${isRessourcesOpen ? "rotate-180" : ""}`} />
          </Link>

          {isRessourcesOpen && (
            <div id="navbar-ressources-panel" className="absolute top-full left-1/2 -translate-x-1/2 pt-2 z-50">
              <div className="bg-surface shadow-xl rounded-2xl border border-gray-100 p-3 flex flex-col gap-1 min-w-[260px]">
                {CATEGORIES.map((category) => {
                  const meta = CATEGORY_META[category];
                  return (
                    <Link
                      key={category}
                      href={`/resources/${categoryToSlug(category)}${ressourcesSuffix}`}
                      onClick={() => { trackEvent("navbar-ressources-item", { category }); setIsRessourcesOpen(false); }}
                      className="flex items-start gap-3 px-3 py-2.5 rounded-xl hover:bg-blue-ulight transition-colors duration-150 group/item"
                    >
                      <meta.Icon className={`mt-0.5 shrink-0 text-base ${meta.color}`} aria-hidden="true" />
                      <div>
                        <p className="text-sm font-semibold text-text-main group-hover/item:text-brand transition-colors leading-tight mb-0.5">
                          <FormattedMessage id={meta.labelKey} />
                        </p>
                        <p className="text-xs text-gray-main"><FormattedMessage id={meta.descKey} /></p>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </div>
          )}
        </li>

        <li className="flex items-center">
          <LanguageSwitcher variant="desktop" />
        </li>

        <li className="flex items-center">
          <Link
            href={appUrl}
            onClick={() => {
              trackEvent("navbar-cta-button", {}, { criticalOnNav: true });
              trackCtaClick("navbar", appTarget);
            }}
            rel="noopener noreferrer"
            className="bg-blue-main border border-secondary-main text-white flex items-center justify-center px-6 py-2 rounded-full transform duration-300 transition-all cursor-pointer group relative"
          >
            <FaArrowRight aria-hidden="true" className="absolute right-2 transform transition-all duration-300 ease-in-out rotate-90 opacity-0 group-hover:rotate-0 group-hover:opacity-100 group-hover:right-5" />
            <span className="transition-all duration-300 ease-in-out group-hover:pr-6">
              <FormattedMessage id="nav.cta" />
            </span>
          </Link>
        </li>
      </ul>
    </nav>
  );
};

export default DesktopNavbar;
